import { createSelector } from 'reselect';
import type {
  CartProduct,
  CheckoutStep,
  PricingState,
  RootState,
} from '../types';
import pricingSelectors from '../state/pricing/selectors';
import cartItemSelectors from '../state/cartItems/selectors';
import { createVariantFromProductAttrs } from './productVariantCreator';
import { cartSelectors } from '../state/cart';
import { CART_STEP_VIEWED_HEADER } from '../constants/tracking';

const getDiscountedNetPrice = (
  pricing: PricingState,
  fractionDigits: number
) => {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectDiscountedTotal(pricing).net / divideBy;
};

const getListNetPrice = (pricing: PricingState, fractionDigits: number) => {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectListTotal(pricing).net / divideBy;
};

const getTax = (pricing: PricingState, fractionDigits: number) => {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectTax(pricing) / divideBy;
};

const getNetDiscountAmount = (
  pricing: PricingState,
  fractionDigits: number
) => {
  const divideBy = 10 ** fractionDigits;
  return pricingSelectors.selectNetCentsDiscountedFromList(pricing) / divideBy;
};

const getNetProductTotal = (pricing: PricingState, fractionDigits: number) => {
  const divideBy = 10 ** fractionDigits;
  return (
    (pricingSelectors.selectProductTotal(pricing)?.netDiscounted || 0) /
    divideBy
  );
};

const selectCartProducts = createSelector(
  [
    (state: Pick<RootState, 'cartItems'>) => state.cartItems,
    (state: Pick<RootState, 'pricing'>) => state.pricing,
  ],
  (cartItems, pricing): CartProduct[] => {
    const fractionDigits = pricingSelectors.selectFractionDigits(pricing);
    const cartLineItems = cartItemSelectors.selectAllItemIds(cartItems);

    return cartLineItems.map((cartItemId: string) => {
      const itemPricing = pricingSelectors.selectPricingDataByItemId(
        pricing,
        cartItemId
      );
      const productAttributes = cartItemSelectors.selectProductAttributeList(
        cartItems,
        cartItemId
      );
      // This function composes item-level analytics data from a mix of two sources:
      //
      //  1. First-class fields the cart knows about
      //  2. Arbitrary bits of data provided in the customerOrderData.analytics field on the line item
      //
      // There are some cases where our fields need to take precedence, and some cases where values provided in
      // the metadata object need to take precedence. The order of operations of this method is very sensitive.

      const container = {
        // First put product_id in, so it can potentially be overridden by metadata someone else provided.
        product_id: cartItemSelectors.selectProductKeyByItemId(
          cartItems,
          cartItemId
        ),
        name: cartItemSelectors.selectProductNameByItemId(
          cartItems,
          cartItemId
        ),

        // Then layer in all the metadata we were provided
        ...cartItemSelectors.selectAnalyticsDataByItemId(cartItems, cartItemId),

        // But for all other fields, our versions take precedence
        // e.g. if someone adds customOrderData.analytics.quantity, we should ignore that and override
        core_product_id: cartItemSelectors.selectProductKeyByItemId(
          cartItems,
          cartItemId
        ),
        quantity: 1, // This should always be 1...stupid segment requirements
        sales_quantity: cartItemSelectors.selectQuantityByItemId(
          cartItems,
          cartItemId
        ),
        price: getDiscountedNetPrice(itemPricing, fractionDigits),
        list_price: getListNetPrice(itemPricing, fractionDigits),
        tax: getTax(itemPricing, fractionDigits),
        attributes: productAttributes,
        mcp_id: cartItemSelectors.selectFulfillmentSkuByItemId(
          cartItems,
          cartItemId
        ),
        design: cartItemSelectors.selectDesignDataByItemId(
          cartItems,
          cartItemId
        ),
        discount: getNetDiscountAmount(itemPricing, fractionDigits),
      };

      // Don't attempt to override the variant field if one was already provided in the metadata
      if (!container.variant) {
        const variant = createVariantFromProductAttrs(productAttributes);

        // And don't attempt to set the field if it's blank (that causes problems)
        if (variant) {
          container.variant = variant;
        }
      }
      return container;
    });
  }
);

const selectCheckoutStepData = createSelector(
  [
    (state: Pick<RootState, 'cart'>) => state.cart,
    (state: Pick<RootState, 'cartItems'>) => state.cartItems,
    (state: Pick<RootState, 'pricing'>) => state.pricing,
  ],
  (cart, cartItems, pricing): CheckoutStep => {
    const fractionDigits = pricingSelectors.selectFractionDigits(pricing);
    const products = selectCartProducts({ cartItems, pricing });
    return {
      checkout_id: cartSelectors.selectCartId(cart),
      currency: pricingSelectors.selectCurrency(pricing),
      coupon: pricingSelectors.selectDiscountCodeName(pricing),
      products,
      discount: getNetDiscountAmount(pricing, fractionDigits),
      revenue: getNetProductTotal(pricing, fractionDigits),
      ...CART_STEP_VIEWED_HEADER,
    };
  }
);

export default {
  selectCheckoutStepData,
};
